import React from 'react';
import 'react-tooltip/dist/react-tooltip.css';

function SinceBeingTrackedSection({ contract, simulationData }) {
  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value);
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    const symbol = num >= 0 ? '↑' : '↓';
    return <span className={color}>{symbol} {Math.abs(num).toFixed(1)}%</span>;
  };

  const formatNumber = (value) => {
    if (value == null || isNaN(value)) return '--';
    return Number(value).toFixed(0);
  };

  const formatDate = (date) => {
    if (!date) return '--';
    return new Date(date).toISOString().slice(0, 10);
  };

  const {
    initial_premium = 0,
    current_premium = 0,
    underlying_price_at_add = 0,
    current_underlying_price = 0,
    initial_equity = 0,
    current_equity = 0,
    number_of_contracts = 1,
    initial_days_to_gain,
    dynamic_days_to_gain,
    premium_percent_change = 0,
    underlying_percent_change = 0,
    equity_percent_change = 0,
    target_percent_change,
    first_added_to_group_date,
    last_reset_date,
    last_refresh_date,
  } = contract;

  // Calculate target progress
  const targetProgress = target_percent_change != null ? {
    actualChange: Number(underlying_percent_change) || 0,
    targetChange: Number(target_percent_change) || 0,
    progress: target_percent_change !== 0 ? (Number(underlying_percent_change) / Number(target_percent_change)) * 100 : 0
  } : null;

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-300 mb-2">📊 Since Being Tracked</h4>
      <div className="bg-gray-800/50 rounded p-2 text-xs space-y-1.5">
        {/* Performance Metrics */}
        <div>
          <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
            📈 Performance
          </div>

          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
            <div className="flex justify-between items-start">
              <span className="text-gray-400 text-[10px] cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Left value is your avg cost per contract when saved. Right is current market premium."
              >Premium:</span>
              <div className="text-right">
                <div className="text-white text-[10px] leading-tight">
                  {formatCurrency(initial_premium)} → {formatCurrency(current_premium)}
                </div>
                <div className="text-[9px] leading-tight">
                  {formatPercent(premium_percent_change)}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-gray-400 text-[10px] cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Left value is the stock price when you started tracking. Right is the current market price."
              >Underlying:</span>
              <div className="text-right">
                <div className="text-white text-[10px] leading-tight">
                  {formatCurrency(underlying_price_at_add)} → {formatCurrency(current_underlying_price)}
                </div>
                <div className="text-[9px] leading-tight">
                  {formatPercent(underlying_percent_change)}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-gray-400 text-[10px] cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Total equity = Contracts x Premium x 100. Shows initial vs current value."
              >Equity:</span>
              <div className="text-right">
                <div className="text-white text-[10px] leading-tight">
                  {formatCurrency(initial_equity)} → {formatCurrency(current_equity)}
                </div>
                <div className="text-[9px] leading-tight">
                  {formatPercent(equity_percent_change)}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-[10px]">Contracts:</span>
              <div className="text-white text-[10px]">{formatNumber(number_of_contracts)}</div>
            </div>
          </div>
        </div>

        {/* Sell Metrics - Only shown for sell positions */}
        {contract.position_type === 'sell' && simulationData && (
          <div className="pt-1.5 border-t border-gray-700">
            <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
              💰 Sell Position
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Collateral Req:</span>
                <div className="text-white text-[10px]">{formatCurrency(simulationData['Collateral Required'])}</div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Return on Collat:</span>
                <div className="text-white text-[10px]">{simulationData['Return on Collateral %'] != null ? `${Number(simulationData['Return on Collateral %']).toFixed(2)}%` : '--'}</div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Max Profit:</span>
                <div className="text-green-400 text-[10px]">{formatCurrency(simulationData['Max Profit'])}</div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Max Loss:</span>
                <div className={`text-[10px] ${simulationData['Max Loss'] === 'Unlimited' ? 'text-orange-400' : 'text-red-400'}`}>
                  {simulationData['Max Loss'] === 'Unlimited' ? 'Unlimited' : formatCurrency(simulationData['Max Loss'])}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Underlying Exit % Progress */}
        {targetProgress && (
          <div className="pt-1.5 border-t border-gray-700">
            <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
              🎯 Underlying Exit % Progress
            </div>

            <div className="space-y-1">
              {/* Progress Bar */}
              <div className="relative w-full bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${
                    targetProgress.progress < 0 ? 'bg-red-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(Math.max(targetProgress.progress, 0), 100)}%` }}
                />
                {targetProgress.progress > 100 && (
                  <div className="absolute -right-1 -top-1 text-yellow-400 text-xs">⚡</div>
                )}
              </div>
              {/* Progress Text */}
              <div className="flex justify-between items-center">
                <span className={`text-[11px] ${targetProgress.actualChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {targetProgress.actualChange >= 0 ? '↑' : '↓'} {Math.abs(targetProgress.actualChange).toFixed(1)}% of {targetProgress.targetChange >= 0 ? '+' : ''}{targetProgress.targetChange.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Time Tracking */}
        <div className="pt-1.5 border-t border-gray-700">
          <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
            ⏳ Time Tracking
          </div>

          <div className="flex justify-between items-start">
            <span className="text-gray-400 text-[10px] cursor-help"
              data-tooltip-id="watchlist-card-tooltip"
              data-tooltip-content="Your exit holding period countdown. 'Exit' is the original days set, 'remaining' counts down daily."
            >Days to Exit:</span>
            <div className="text-right">
              <div className="text-white text-[10px] leading-tight">
                {formatNumber(initial_days_to_gain)} exit | {formatNumber(dynamic_days_to_gain)} remaining
              </div>
              {initial_days_to_gain > 0 && (
                <div className="text-[9px] text-blue-400 leading-tight">
                  {Math.round(((initial_days_to_gain - dynamic_days_to_gain) / initial_days_to_gain) * 100)}% elapsed
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Date Tracking */}
        <div className="pt-1.5 border-t border-gray-700">
          <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
            📅 Date Tracking
          </div>

          <div className="space-y-0.5">
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-[10px] cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Date this contract was first saved to a watchlist group."
              >Added:</span>
              <div className="text-white text-[10px]">{formatDate(first_added_to_group_date)}</div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-[10px] cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Date the days-to-exit countdown was last reset."
              >Exit Reset:</span>
              <div className="text-white text-[10px]">{formatDate(last_reset_date)}</div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-[10px] cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Date market data (premium, underlying, greeks) was last fetched."
              >Data Refreshed:</span>
              <div className="text-white text-[10px]">{formatDate(last_refresh_date)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SinceBeingTrackedSection;